/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-hero-legacy.html";
import { events } from "@nuskin/ns-util";

import "@nuskin/ns-style-elements";

/**
 * The ns-hero-legacy displays a large image that has some text and a cta on a scrim.
 *
 * <h2>UX/Design Docs</h2>
 * <p>Need to find</p>
 *
 * <h1>Demo</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-hero-legacy cta="expressive-link"></ns-hero-legacy>
 *     <ns-hero-legacy background-color="rgb(0, 256, 256)"
 *              scrim-copy-color="dark"
 *              scrim-align="right"
 *              cta-button-theme="tertiary-hit"
 *              adaptive-image-set='{
 *                                     "mobile": "https://via.placeholder.com/767x250",
 *                                     "tablet": "https://via.placeholder.com/1023x450",
 *                                     "desktop": "https://via.placeholder.com/1440x450"
 *                                  }'
 *               headline-copy="Motorcycles" body-copy="So cool and fun to ride. This one looks like a fighter jet.">
 *       <div slot="headline-copy">Motorcycles</div>
 *       <div slot="body-copy">They are so cool and fun to ride. This one looks like a fighter jet.</div>
 *     </ns-hero-legacy>
 *     <ns-hero-legacy background-color="#78aa34"
 *              scrim-copy-color="dark"
 *              scrim-align="left"
 *              cta-button-theme="tertiary-hit"
 *              adaptive-image-set='{
 *                                     "mobile": "https://via.placeholder.com/140x100",
 *                                     "tablet": "https://via.placeholder.com/319x319",
 *                                     "desktop": "https://via.placeholder.com/1280x700"
 *                                  }'></ns-hero-legacy>
 *
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<ns-expressive-link>` provides the following custom properties and mixins for styling:
 *
 *      Custom property            | Description                                                 | Default
 *      :--------------------------|:------------------------------------------------------------|:-------------------
 *      `--ns-hero-bg-color`       | Variable to set the background color of the hero            | `rgba(256, 256, 256, 1)`
 *      `--ns-hero-bg-color`       | Variable to set the background color of the scrim           | `rgba(256, 256, 256, .85)`
 *      `--ns-hero-bg-opacity`     | Variable applied to the hero's background opacity           | `1`
 *      `--ns-hero-mobile-bg`      | Variable applied to the hero's adaptive image for mobile    | `img/dev-portal/src/img/glow-girl.png`
 *      `--ns-hero-tablet-bg`      | Variable applied to the hero's adaptive image for tablet    | `img/dev-portal/src/img/glow-girl.png`
 *      `--ns-hero-desktop-bg`     | Variable applied to the hero's adaptive image for desktop   | `img/dev-portal/src/img/glow-girl.png`
 *      `--ns-hero-bg-opacity`     | Variable applied to the hero's background opacity           | `1`
 *
 *      ### Slots
 *
 *      ##### `<ns-expressive-link>` provides the following slots for content:
 *
 *      Custom property                 | Description                           | Default
 *      :-------------------------------|:--------------------------------------|:-------------------
 *      `headline-copy`                 | Content of the headline on the scrim   | `'Sample Headline'`
 *      `body-copy`                     | Content of the body on the scrim       | `'Sample body copy for the ns-hero'`
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsHeroLegacy extends PolymerElement {
  constructor() {
    super();
  }

  ready() {
    super.ready();
    this.$.heroButton.addEventListener(
      "click",
      this.processHeroClick.bind(this)
    );
    this.$.heroLink.addEventListener("click", this.processHeroClick.bind(this));
    window.addEventListener("resize", () => {
      this._setAdaptiveImages();
      this.resizeScrimHeaderText();
    });
    this.resizeScrimHeaderText();
  }

  static get is() {
    return "ns-hero-legacy";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * A set of images that will dynamically be set for mobile, tablet and desktop.
       * You must specify all three.
       */
      adaptiveImageSet: {
        type: Object,
        observer: "_setAdaptiveImages"
      },
      /**
       * Sets the background color of the container and the scrim.
       * Converts hex values to RGB so the scrim can be transparent
       * but you can also use rgb or rgba values.
       */
      backgroundColor: {
        type: String,
        value: "#FFFFFF",
        observer: "_setColors"
      },
      /**
       * Sets a background image from the adaptiveImageSet
       */
      backgroundImage: {
        type: String,
        value: ""
      },
      /**
       * Sets the body copy
       */
      bodyCopy: {
        type: String,
        value: "Sample body copy for the ns-hero-legacy"
      },
      /**
       * The style of the cta. Options are: button, expressive-link.
       */
      cta: {
        type: String,
        value: "button"
      },
      /**
       * If the cta is a button this sets the button's theme. See themes in ns-button docs for options.
       */
      ctaButtonTheme: {
        type: String,
        value: "secondary-inverse"
      },
      /**
       * Use the inverted ns-expressive-link. Options are: inverted.
       */
      ctaLinkTheme: {
        type: String,
        value: ""
      },
      /**
       * Set the destination if the user clicks the cta.
       */
      ctaLink: {
        type: String,
        value: "https://www.nuskin.com"
      },
      /**
       * Sets the font-family for the headline. Options are: Proxima Nova, Freeland
       */
      headlineFontFamily: {
        type: String
      },
      /**
       * Sets the headline copy
       */
      headlineCopy: {
        type: String,
        value: "Sample Headline"
      },
      /**
       * Determines how the scrim should be aligned with the container. Options are: center, left, right.
       */
      scrimAlign: {
        type: String,
        value: "left"
      },
      /**
       * Sets the theme of the scrim copy for use on either dark images or light images. Options are: dark, light.
       */
      scrimCopyColor: {
        type: String,
        value: "dark",
        observer: "_setTheme",
        reflectToAttribute: true
      },
      /**
       * Sets the translations for the element.
       */
      translations: {
        type: Object,
        value: {
          ctaCopy: "CTA Copy"
        }
      },
      /**
       * Alt text for the Hero Image
       */
      imageAltText: {
        type: String,
        value: "Image Alt Text"
      },
      analyticsId: {
        type: String,
        value: null
      }
    };
  }

  resizeScrimHeaderText() {
    const elements = this.shadowRoot.querySelectorAll(".hero__scrim-header");

    elements.forEach((el) => {
      resizeTextToFit(el, 10, true);
    });

    function resizeTextToFit(el, minSize = 10, checkHeight = false) {
      el.style.removeProperty("font-size");
      el.style.removeProperty("line-height");
      resize(el, minSize, checkHeight);

      function resize(el, minSize, checkHeight) {
        let shouldResize = el.scrollWidth > el.offsetWidth;

        // if width is fine, check height if requested
        if (checkHeight && !shouldResize) {
          const parentEl = el.parentElement;
          if (parentEl) {
            shouldResize = parentEl.scrollHeight > parentEl.offsetHeight;
          }
        }

        // if element width is greater (or optionally taller) than container, reduce fontsize
        if (shouldResize) {
          const styles = window.getComputedStyle(el);
          const newFontSize =
            parseInt(styles.getPropertyValue("font-size").slice(0, -2)) - 1;
          el.style["font-size"] = newFontSize + "px";
          el.style["line-height"] = "normal";

          if (newFontSize > minSize) {
            resize(el, minSize, checkHeight);
          }
        }
      }
    }
  }

  /**
   * Sets the background and scrim color of the hero
   * @param {*} value
   * A hex, rgb or rgba value
   */
  _setColors(value) {
    if (value && value.includes("#")) {
      const results = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value);
      if (results) {
        this.updateStyles({
          "--ns-hero-bg-color":
            "rgba(" +
            parseInt(results[1], 16) +
            "," +
            parseInt(results[2], 16) +
            "," +
            parseInt(results[3], 16) +
            ", 1)",
          "--ns-hero-scrim-color":
            "rgba(" +
            parseInt(results[1], 16) +
            "," +
            parseInt(results[2], 16) +
            "," +
            parseInt(results[3], 16) +
            ", .85)"
        });
      }
    } else if (value && value.includes("rgb")) {
      const results = value.replace(/[^\d,]/g, "").split(",");
      const alpha = results[3] ? "." + results[3] : ".85";
      this.updateStyles({
        "--ns-hero-bg-color":
          "rgba(" + results[0] + "," + results[1] + "," + results[2] + ", 1)",
        "--ns-hero-scrim-color":
          "rgba(" +
          results[0] +
          "," +
          results[1] +
          "," +
          results[2] +
          "," +
          alpha +
          ")"
      });
    }
  }

  /**
   * Sets the adaptive images for the hero
   */
  _setAdaptiveImages() {
    this.backgroundImage =
      window.outerWidth >= 1024
        ? this.adaptiveImageSet.desktop
        : window.outerWidth >= 768
          ? this.adaptiveImageSet.tablet
          : this.adaptiveImageSet.mobile;
    // this.updateStyles({
    //     '--ns-hero-mobile-bg': 'url("' + this.adaptiveImageSet.mobile + '")',
    //     '--ns-hero-tablet-bg': 'url("' + this.adaptiveImageSet.tablet + '")',
    //     '--ns-hero-desktop-bg': 'url("' + this.adaptiveImageSet.desktop + '")'
    // });
  }

  /**
   * Sets the theme for the text on the scrim
   */
  _setTheme() {
    if (this.scrimCopyColor === "dark") {
      this.ctaButtonTheme = "secondary";
    } else {
      this.ctaLinkTheme = "invert";
      this.ctaButtonTheme = "secondary-inverse";
    }
  }

  processHeroClick() {
    if (!!this.analyticsId && this.analyticsId.length > 0)
      events.publish(events.shop.NS_HERO_CLICKED, { data: this.analyticsId });
    window.location.href = this.ctaLink;
  }
}
customElements.define(NsHeroLegacy.is, NsHeroLegacy);
